/**
 * React Query hooks for sender prompt groups CRUD
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import api from '@/services/api';

import type {
  SenderPromptGroup,
  CreateGroupPayload,
  UpdateGroupPayload,
  ReorderNestedPromptGroupsPayload,
} from '../types/campaign-texts.types';
import { normalizeSenderPromptGroup } from '../utils/normalize-sender-group';

const SENDER_GROUPS_KEY = ['campaign-texts', 'sender-groups'] as const;

export function useSenderPromptGroups() {
  return useQuery<SenderPromptGroup[]>({
    queryKey: [...SENDER_GROUPS_KEY],
    queryFn: async () => {
      const raw = await api.get('/campaign-texts/sender-groups').then((r) => r.data.data);
      return (raw as SenderPromptGroup[]).map((g) => normalizeSenderPromptGroup(g));
    },
    staleTime: 30_000,
  });
}

export function useCreateSenderGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateGroupPayload) =>
      api.post('/campaign-texts/sender-groups', payload).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...SENDER_GROUPS_KEY] }),
  });
}

export function useUpdateSenderGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: UpdateGroupPayload & { id: string }) =>
      api.patch(`/campaign-texts/sender-groups/${id}`, payload).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...SENDER_GROUPS_KEY] }),
  });
}

export function useDeleteSenderGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete(`/campaign-texts/sender-groups/${id}`).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...SENDER_GROUPS_KEY] }),
  });
}

export function useReorderNestedSenderGroups() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ReorderNestedPromptGroupsPayload) =>
      api
        .patch('/campaign-texts/sender-groups/reorder-nested', {
          parentId: payload.parentId ?? null,
          orderedIds: payload.orderedIds,
        })
        .then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...SENDER_GROUPS_KEY] }),
  });
}
